/**
 * Sport-neutral event log types.
 *
 * Nothing in this file knows what a basket is. Basketball concepts live in
 * @good-season/stats-basketball (D15, ADR-0005). The shape here is the one
 * defined in BUILD_PLAN.md section 5.3, minus the fields that only the
 * database and the sync layer care about.
 */

/** Which side an event belongs to. Sport-neutral. */
export type Side = 'us' | 'them';

/**
 * Control events every sport with a clock and substitutions needs. Sport
 * specific event types are plain strings supplied by the sport package, which
 * is what keeps core from growing a basketball vocabulary.
 */
export const CoreEventType = {
  SUB_IN: 'SUB_IN',
  SUB_OUT: 'SUB_OUT',
  PERIOD_START: 'PERIOD_START',
  PERIOD_END: 'PERIOD_END',
  CLOCK_START: 'CLOCK_START',
  CLOCK_STOP: 'CLOCK_STOP',
  SET_CLOCK: 'SET_CLOCK',
  TIMEOUT_FULL: 'TIMEOUT_FULL',
  TIMEOUT_SHORT: 'TIMEOUT_SHORT',
} as const;

export type CoreEventType = (typeof CoreEventType)[keyof typeof CoreEventType];

/**
 * One recorded event. Immutable: a correction is a void plus a new event, never
 * a mutation (CLAUDE.md, "Append-only, never mutate"; REVIEW_RUBRIC A1).
 *
 * `gameClockMs` is milliseconds REMAINING in the period, matching how a
 * scoreboard reads and how BUILD_PLAN 5.3 defines it. It counts down.
 */
export interface CoreEvent {
  /** Client-generated UUID. The idempotency key for sync (REVIEW_RUBRIC A2). */
  readonly id: string;
  /** Monotonic per device. Orders events recorded at the same clock reading. */
  readonly clientSeq: number;
  /** Which device recorded it. Last resort tiebreak, and merge-review input. */
  readonly deviceId: string;
  readonly period: number;
  /** Milliseconds remaining in the period. Counts down. */
  readonly gameClockMs: number;
  /** Real-world time, ISO 8601. Kept for conflict resolution and D11 film sync. */
  readonly wallClock: string;
  readonly type: string;
  readonly team: Side;
  /** Null for team events and for unattributed plays (BUILD_PLAN 5.7). */
  readonly playerId: string | null;
  readonly value: number | null;
  readonly meta: Readonly<Record<string, unknown>>;
  /** Set when this event has been corrected. A voided event never counts. */
  readonly voidedAt: string | null;
}

/** Period lengths. Overtime periods may differ from regulation. */
export interface PeriodStructure {
  /** Regulation period count. 2 for halves, 4 for quarters (D2 defaults to 2). */
  readonly regulationPeriods: number;
  readonly regulationLengthMs: number;
  readonly overtimeLengthMs: number;
}

export interface CoreGameConfig {
  readonly periods: PeriodStructure;
  /** 3, 4, or 5. Youth leagues genuinely vary (BUILD_PLAN 5.2). */
  readonly onCourtCount: number;
  /**
   * Running clock leagues do not stop the clock on dead balls, so CLOCK_STOP
   * and CLOCK_START events are advisory rather than structural.
   */
  readonly runningClock: boolean;
}

/**
 * A continuous span during which the same set of players was on the floor.
 * Everything about minutes and plus-minus derives from these.
 */
export interface Stint {
  readonly period: number;
  /** Milliseconds elapsed in the game when the stint began. */
  readonly startElapsedMs: number;
  /** Milliseconds elapsed in the game when the stint ended. */
  readonly endElapsedMs: number;
  readonly durationMs: number;
  /** Player IDs on the floor for the whole stint, sorted for stable comparison. */
  readonly lineup: readonly string[];
}

/**
 * A problem found while deriving state. Warnings never block scoring
 * (BUILD_PLAN 5.3, "Invariants enforced with warnings, not blocks"). They
 * surface in the UI and in the phase report, and a scorekeeper can ignore
 * every one of them and still finish the game.
 */
export interface Warning {
  readonly code: WarningCode;
  /** Human-readable, already written as user-facing copy (CLAUDE.md C2/C3). */
  readonly message: string;
  readonly period: number | null;
  readonly eventId: string | null;
  readonly meta: Readonly<Record<string, unknown>>;
}

export const WarningCode = {
  /** More or fewer players on the floor than the configured count. */
  LINEUP_COUNT: 'LINEUP_COUNT',
  /** A SUB_IN for a player already on the floor, or SUB_OUT for one who is not. */
  SUB_INCONSISTENT: 'SUB_INCONSISTENT',
  /** An event arrived with a clock reading later than the one before it. */
  CLOCK_NONMONOTONIC: 'CLOCK_NONMONOTONIC',
  /** A period has events but no PERIOD_START, so its stints are inferred. */
  PERIOD_UNOPENED: 'PERIOD_UNOPENED',
  /** Derived minutes and elapsed game time disagree beyond tolerance. */
  MINUTES_RECONCILIATION: 'MINUTES_RECONCILIATION',
  /** An event was recorded for a player who never entered the game. */
  PLAYER_NOT_IN_GAME: 'PLAYER_NOT_IN_GAME',
} as const;

export type WarningCode = (typeof WarningCode)[keyof typeof WarningCode];
